"use client";

import { useState } from "react";
import { X, CheckCircle, Headphones, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { TELECALLING_CAMPAIGNS, calculateIndicativeRetainer } from "@/lib/demos/telecallingData";

interface CampaignProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    campaignId: string;
    seatsCount: number;
    shiftType: "day" | "night" | "247";
    monthlyRetainer: number;
  };
  currency: "INR" | "USD";
  onSuccess?: () => void;
}

export function CampaignProposalModal({
  isOpen,
  onClose,
  initialData,
  currency,
  onSuccess,
}: CampaignProposalModalProps) {
  const [campaignId, setCampaignId] = useState(initialData?.campaignId || "b2b-outbound");
  const [seatsCount, setSeatsCount] = useState(initialData?.seatsCount || 6);
  const [shiftType, setShiftType] = useState<"day" | "night" | "247">(initialData?.shiftType || "day");
  
  const [prospectName, setProspectName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedLeadNo, setSubmittedLeadNo] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen) return null;

  const activeCampaign = TELECALLING_CAMPAIGNS.find((c) => c.id === campaignId) || TELECALLING_CAMPAIGNS[0];
  const calculation = calculateIndicativeRetainer(campaignId, seatsCount, shiftType, currency);
  const sym = currency === "USD" ? "$" : "₹";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await fetch("/api/demos/telecalling/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prospect_name: prospectName,
          company_name: companyName,
          email,
          phone,
          campaign_id: campaignId,
          campaign_name: activeCampaign.title,
          seats_count: seatsCount,
          shift_type: shiftType,
          currency,
          monthly_retainer: calculation.monthlyTotal,
          notes: notes || "Configured via online seat calculator.",
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to submit proposal request");
      }

      setSubmittedLeadNo(data.lead.lead_no);
      setIsSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setErrorMessage(err.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 shadow-2xl shadow-cyan-950/50 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Proposal Generated!</h3>
            <p className="text-sm text-slate-300 mb-4 leading-relaxed">
              We have generated campaign docket <strong className="text-cyan-400">{submittedLeadNo}</strong> and queued your official Proposal Illustration via Hostinger SMTP to <strong className="text-white">{email}</strong>.
            </p>

            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-left text-xs space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-slate-400">Campaign:</span>
                <span className="font-semibold text-white">{activeCampaign.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Dedicated Headcount:</span>
                <span className="font-semibold text-cyan-400">{seatsCount} Calling Seats</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Monthly Retainer:</span>
                <span className="font-bold text-emerald-400">{sym}{calculation.monthlyTotal.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/demos/telecalling/crm"
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20"
              >
                <span>Track in Calling Floor CRM</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={onClose}
                className="py-3 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-colors"
              >
                Close Window
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Configure BPO Campaign Pilot</h3>
                <p className="text-xs text-slate-400">Lock headcount &amp; trigger automated proposal</p>
              </div>
            </div>

            {errorMessage && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs mb-4">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Campaign summary pill */}
              <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between text-xs">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">Selected Track</span>
                  <span className="font-bold text-white">{activeCampaign.title}</span>
                </div>
                <div className="text-right">
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">Retainer Quote</span>
                  <span className="font-bold text-cyan-400">{sym}{calculation.monthlyTotal.toLocaleString()}/mo</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Decision Maker Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rahul Mehra"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Nexus Cloud Solutions"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Corporate Email *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. rahul@nexuscloud.io"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Direct Phone / WhatsApp
                  </label>
                  <input
                    type="tel"
                    placeholder="+91 98450 11234"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              {/* Headcount adjustment inside modal */}
              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Dedicated Seats
                  </label>
                  <select
                    value={seatsCount}
                    onChange={(e) => setSeatsCount(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500"
                  >
                    {[2, 3, 5, 8, 10, 15, 20, 30].map((n) => (
                      <option key={n} value={n}>
                        {n} Callers (Pod)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Shift Coverage
                  </label>
                  <select
                    value={shiftType}
                    onChange={(e) => setShiftType(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500"
                  >
                    <option value="day">Day Shift (9 AM - 6 PM)</option>
                    <option value="night">US / Night Shift</option>
                    <option value="247">24x7 Coverage</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Target Audience or Campaign Notes
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Target B2B SaaS CTOs in North America. Need warm transfer to Account Executives."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs tracking-wide shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating Proposal Docket...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit &amp; Dispatch Proposal Docket</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              <div className="text-center">
                <span className="text-[10px] text-slate-500 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-cyan-400" />
                  Data secured under SOC-2 Type II standards. Instant email dispatch via Hostinger SMTP.
                </span>
              </div>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
